import React from 'react';
import { getLogoSrc } from '../utils/logoHelper';
import { formatGameDate, formatGameTime } from '../utils/dateFormatter';
import api from '../services/api';

const API_BASE_URL = api.defaults.baseURL.replace('/api', '');
const W = 1080;
const H = 1080;
const MAX_H = 1350;

function GraphicTryoutInfoCard({
  graphicData,
  teamSport,
  teamColors,
  backgroundStyle = 'linear-gradient',
  homeTeamLogo,
  showWatermark = true,
  dateFormat = 'full',
  heroFont = 'Bebas Neue',
  heroFontSize = 160,
  accentBlockColor = null,
  detailTextColor = null,
}) {
  const {
    homeTeam, ageGroup, tryoutDate, tryoutDate2,
    tryoutTime, location, contact, additionalInfo,
  } = graphicData;

  const accent = accentBlockColor || teamColors.primary;
  const detailColor = detailTextColor || teamColors.primary;

  const isLightColor = (color) => {
    if (!color || color === 'null') return false;
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 155;
  };

  const getBackground = () => {
    const p = teamColors.primary;
    const s = teamColors.secondary;
    switch (backgroundStyle) {
      case 'radial-gradient': return `radial-gradient(circle at center, ${p} 0%, ${s} 100%)`;
      case 'diagonal-gradient': return `linear-gradient(45deg, ${p} 0%, ${s} 100%)`;
      case 'split-two-tone': return `linear-gradient(180deg, ${p} 0%, ${p} 50%, ${s} 50%, ${s} 100%)`;
      case 'solid-primary': return p;
      case 'solid-secondary': return s;
      default: return `linear-gradient(135deg, ${p} 0%, ${s} 100%)`;
    }
  };

  const headerTextColor = isLightColor(teamColors.primary) ? '#1a202c' : '#ffffff';
  const tryoutsSize = Math.max(100, Math.min(heroFontSize, 180));
  const teamNameSize = (homeTeam || 'TEAM NAME').length > 14 ? 56 : (homeTeam || 'TEAM NAME').length > 9 ? 68 : 84;

  const logoCircleStyle = {
    width: 140,
    height: 140,
    borderRadius: '50%',
    background: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  };

  const detailRowStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 24,
    padding: '28px 0',
    borderBottom: '1px solid #e2e8f0',
  };

  const detailLabelStyle = {
    fontFamily: 'Barlow, sans-serif',
    fontSize: 22,
    fontWeight: 700,
    color: accent,
    letterSpacing: 3,
    textTransform: 'uppercase',
    minWidth: 180,
    flexShrink: 0,
    paddingTop: 4,
  };

  const detailValueStyle = {
    fontFamily: 'Barlow, sans-serif',
    fontSize: 36,
    fontWeight: 600,
    color: detailColor,
    lineHeight: 1.3,
  };

  return (
    <div
      id="graphic-tryout"
      style={{
        width: W,
        minHeight: H,
        maxHeight: MAX_H,
        height: 'auto',
        position: 'relative',
        overflow: 'visible',
        borderRadius: 16,
        border: '3px solid #94a3b8',
        fontFamily: 'Barlow, sans-serif',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header Band */}
      <div style={{
        background: getBackground(),
        padding: '56px 80px 48px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <div>
          <div style={{
            fontFamily: heroFont,
            fontSize: tryoutsSize,
            fontWeight: 900,
            color: headerTextColor,
            lineHeight: 0.9,
            letterSpacing: 6,
          }}>
            TRYOUTS
          </div>
          <div style={{
            fontFamily: 'Bebas Neue, sans-serif',
            fontSize: teamNameSize,
            color: headerTextColor,
            opacity: 0.85,
            letterSpacing: 3,
            marginTop: 8,
          }}>
            {homeTeam || 'TEAM NAME'}
          </div>
        </div>
        <div style={logoCircleStyle}>
          {homeTeamLogo ? (
            <img
              src={getLogoSrc(homeTeamLogo, API_BASE_URL)}
              alt={homeTeam}
              style={{ width: 'auto', height: 'auto', maxWidth: '80%', maxHeight: '80%' }}
            />
          ) : (
            <span style={{ fontSize: 72 }}>
              {teamSport === 'Softball' ? '🥎' : '⚾'}
            </span>
          )}
        </div>
      </div>

      {/* Age Group Banner */}
      <div style={{
        background: accent,
        padding: '24px 80px',
        display: 'flex',
        alignItems: 'center',
        gap: 24,
        flexShrink: 0,
      }}>
        <span style={{
          fontFamily: 'Barlow, sans-serif',
          fontSize: 24,
          fontWeight: 700,
          color: isLightColor(accent) ? '#1a202c' : 'rgba(255,255,255,0.8)',
          letterSpacing: 4,
          textTransform: 'uppercase',
        }}>
          Looking For:
        </span>
        <span style={{
          fontFamily: 'Bebas Neue, sans-serif',
          fontSize: 52,
          color: isLightColor(accent) ? '#1a202c' : '#ffffff',
          letterSpacing: 3,
        }}>
          {ageGroup || 'ALL AGES'}
        </span>
      </div>

      {/* Info Panel */}
      <div style={{
        background: '#ffffff',
        padding: '16px 80px 40px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
      }}>
        {(tryoutDate || tryoutDate2) && (
          <div style={detailRowStyle}>
            <span style={detailLabelStyle}>Date</span>
            <span style={detailValueStyle}>
              {tryoutDate ? formatGameDate(tryoutDate, dateFormat) : ''}
              {tryoutDate && tryoutDate2 ? ' & ' : ''}
              {tryoutDate2 ? formatGameDate(tryoutDate2, dateFormat) : ''}
            </span>
          </div>
        )}
        {tryoutTime && (
          <div style={detailRowStyle}>
            <span style={detailLabelStyle}>Time</span>
            <span style={detailValueStyle}>{formatGameTime(tryoutTime)}</span>
          </div>
        )}
        {location && (
          <div style={detailRowStyle}>
            <span style={detailLabelStyle}>Location</span>
            <span style={detailValueStyle}>{location}</span>
          </div>
        )}
        {contact && (
          <div style={detailRowStyle}>
            <span style={detailLabelStyle}>Register</span>
            <span style={detailValueStyle}>{contact}</span>
          </div>
        )}
        {additionalInfo && (
          <div style={{ ...detailRowStyle, borderBottom: 'none' }}>
            <span style={detailLabelStyle}>Info</span>
            <span style={{ ...detailValueStyle, fontSize: 28, opacity: 0.8 }}>
              {additionalInfo}
            </span>
          </div>
        )}
      </div>

      {/* Watermark */}
      {showWatermark && (
        <div style={{
          position: 'absolute', bottom: 12, right: 12,
          background: 'rgba(255,255,255,0.92)', padding: '6px 12px',
          borderRadius: 8, pointerEvents: 'none', zIndex: 3,
        }}>
          <img src="/PMlogoleft.png" alt="Playmaker Media Kit" style={{ height: 80, width: 'auto', display: 'block' }} />
        </div>
      )}
    </div>
  );
}

export default GraphicTryoutInfoCard;
